import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  IconButton,
  Chip,
  Stack,
  Button,
  Checkbox,
  Menu,
  MenuItem,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  Grow,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Search,
  FilterList,
  Sort,
  Edit,
  Delete,
  Visibility,
  Add,
  ContentCopy,
  MoreVert,
  Payments,
  MoveToInbox,
} from "@mui/icons-material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import DeleteAssetDialog from "./DeleteAssetDialog";
import WarningDialog, {
  WarningMessage,
} from "../../../utils/dialog/WarningDialog";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { StyledIconButton } from "../../../utils/Icon";
import { calculateTotalPrincipalPaid } from "./PrincipalPaidTable";

const AssetListView = ({
  assets,
  importableAssets,
  isFullscreen,
  onEditAsset,
  onDeleteAsset,
  onViewAsset,
  onAddAsset,
  onDuplicateAsset,
  onDefinePrincipalPaid,
  selectedAssetId,
  onSelectAsset,
  onBulkDeleteAssets,
  onBulkDisposeAssets,
  onImport,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // State for search, filter, and sort
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCaType, setFilterCaType] = useState("all");
  const [sortBy, setSortBy] = useState("assetName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuAssetId, setActionMenuAssetId] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDisposeDialogOpen, setBulkDisposeDialogOpen] = useState(false);

  // Get unique CA types for filter
  const caTypes = useMemo(() => {
    const types = [...new Set(assets.map((asset) => asset.caType))];
    return types.filter(Boolean);
  }, [assets]);

  // Filter and sort assets
  const filteredAndSortedAssets = useMemo(() => {
    let filtered = assets.filter((asset) => {
      const matchesSearch = asset.assetName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterCaType === "all" || asset.caType === filterCaType;
      return matchesSearch && matchesFilter;
    });

    // Sort assets
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle numeric values
      if (["cost", "noOfYears", "initialAllowanceRate"].includes(sortBy)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle string values
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [assets, searchTerm, filterCaType, sortBy, sortOrder]);

  // Handle bulk selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedAssets(filteredAndSortedAssets.map((asset) => asset.assetId));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (assetId) => {
    setSelectedAssets((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setSortMenuAnchor(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = () => {
    if (onBulkDeleteAssets) {
      onBulkDeleteAssets(selectedAssets);
    }
    setSelectedAssets([]);
    setBulkDeleteDialogOpen(false);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteDialogOpen(false);
  };

  // Handle bulk dispose
  const handleBulkDispose = () => {
    setBulkDisposeDialogOpen(true);
  };

  const handleConfirmBulkDispose = () => {
    if (onBulkDisposeAssets) {
      onBulkDisposeAssets(selectedAssets);
    }
    setSelectedAssets([]);
    setBulkDisposeDialogOpen(false);
  };

  const handleCancelBulkDispose = () => {
    setBulkDisposeDialogOpen(false);
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? "↑" : "↓";
  };

  // Calculate total principal paid for an asset
  const getTotalPrincipalPaid = (asset) => {
    if (!asset.principalPaid || !Array.isArray(asset.principalPaid)) return 0;
    return calculateTotalPrincipalPaid(asset.principalPaid);
  };

  const getBulkDisposeButtonTitle = (selectedAssetIds) => {
    if (selectedAssetIds.length === 0) return "No Assets Selected";

    const selectedAssets = assets.filter((asset) =>
      selectedAssetIds.includes(asset.assetId)
    );

    const allDisposed = selectedAssets.every(
      (asset) => asset.isCurrentYearDisposed
    );
    const noneDisposed = selectedAssets.every(
      (asset) => !asset.isCurrentYearDisposed
    );

    if (allDisposed) {
      return `Undo Disposal for ${selectedAssets.length} Asset(s)`;
    } else if (noneDisposed) {
      return `Mark ${selectedAssets.length} Asset(s) as Disposed`;
    } else {
      return `Toggle Disposal for ${selectedAssets.length} Asset(s)`;
    }
  };

  const getBulkDisposeDialogMessage = (selectedAssetIds) => {
    const selected = assets.filter((a) => selectedAssetIds.includes(a.assetId));
    const toDispose = selected.filter((a) => !a.isCurrentYearDisposed).length;
    const toUndo = selected.filter((a) => a.isCurrentYearDisposed).length;

    if (selected.length === 0) return "";

    if (toDispose > 0 && toUndo > 0) {
      return `You have selected ${selected.length} assets with mixed statuses. This action will toggle the disposal status of each asset for the current year. Do you want to continue?`;
    }

    if (toDispose === selected.length) {
      return `Are you sure you want to mark all ${selected.length} selected asset(s) as disposed for the current year?`;
    }

    if (toUndo === selected.length) {
      return `Are you sure you want to undo disposal for all ${selected.length} selected asset(s) for the current year?`;
    }

    return `Are you sure you want to update the disposal status of the selected assets?`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        height: isFullscreen ? "88vh" : "82vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 2 }}
        >
          <Typography variant='h6' fontWeight={600} color='primary.main'>
            Asset Management ({filteredAndSortedAssets.length} assets)
          </Typography>

          <Stack direction='row' spacing={1}>
            <StyledIconButton
              icon={MoveToInbox}
              iconSize='24'
              onClick={onImport}
              tooltip='Import Assets from FA Additions'
              iconColor='primary.main'
              badgeContent={importableAssets.length}
            />
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={onAddAsset}
            >
              Add Asset
            </Button>
          </Stack>
        </Stack>

        {/* Search and Filter Bar */}
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          spacing={2}
          alignItems={isSmallScreen ? "stretch" : "center"}
        >
          <TextField
            placeholder='Search assets...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{
              flexGrow: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            size='small'
          />

          <Button
            variant='outlined'
            startIcon={<FilterList />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {filterCaType === "all" ? "All Types" : filterCaType}
          </Button>

          <Button
            variant='outlined'
            startIcon={<Sort />}
            onClick={(e) => setSortMenuAnchor(e.currentTarget)}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Sort: {sortBy}
          </Button>

          {selectedAssets.length > 0 && (
            <Stack direction='row' spacing={1}>
              {selectedAssets.length > 0 && (
                <Button
                  variant='contained'
                  startIcon={<ChangeCircleIcon />}
                  onClick={handleBulkDispose}
                  sx={{
                    backgroundColor: (theme) => theme.palette.warning.main,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.warning.dark,
                    },
                  }}
                >
                  {getBulkDisposeButtonTitle(selectedAssets)}
                </Button>
              )}
              <Button
                variant='contained'
                startIcon={<Delete />}
                onClick={handleBulkDelete}
                sx={{
                  backgroundColor: (theme) => theme.palette.error.main,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.error.dark,
                  },
                }}
              >
                Delete Selected ({selectedAssets.length})
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Asset Table */}
      <TableContainer sx={{ flex: 1, overflow: "auto" }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={
                    selectedAssets.length === filteredAndSortedAssets.length &&
                    filteredAndSortedAssets.length > 0
                  }
                  indeterminate={
                    selectedAssets.length > 0 &&
                    selectedAssets.length < filteredAndSortedAssets.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography variant='subtitle2' fontWeight={600}>
                    Asset Name {getSortIcon("assetName")}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align='center'>
                <Typography variant='subtitle2' fontWeight={600}>
                  CA Type {getSortIcon("caType")}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography variant='subtitle2' fontWeight={600}>
                  Cost {getSortIcon("cost")}
                </Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography variant='subtitle2' fontWeight={600}>
                  No. of years of claim {getSortIcon("noOfYears")}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography variant='subtitle2' fontWeight={600}>
                  Principal Paid
                </Typography>
              </TableCell>
              <TableCell align='right' width={200}>
                <Typography variant='subtitle2' fontWeight={600}>
                  Disposal Status
                </Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography variant='subtitle2' fontWeight={600}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedAssets.map((asset) => {
              const isSelected = selectedAssets.includes(asset.assetId);
              const isCurrentlySelected = selectedAssetId === asset.assetId;

              return (
                <TableRow
                  key={asset.assetId}
                  hover
                  selected={isCurrentlySelected}
                  sx={{
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                    bgcolor: isCurrentlySelected
                      ? (theme) => alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                  }}
                >
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectAsset(asset.assetId)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      fontWeight={isCurrentlySelected ? 600 : 400}
                      color={
                        isCurrentlySelected ? "primary.main" : "text.primary"
                      }
                      sx={{ cursor: "pointer" }}
                      onClick={() => onSelectAsset(asset.assetId)}
                    >
                      {asset.assetName || "Unnamed Asset"}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={asset.caType || "Not Set"}
                      size='small'
                      variant={asset.caType ? "filled" : "outlined"}
                      color={asset.caType ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='body2'>
                      {asset.cost ? formatAmount(asset.cost) : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography variant='body2'>
                      {asset.noOfYears || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='body2'>
                      {formatAmount(getTotalPrincipalPaid(asset))}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Chip
                      label={
                        asset.isCurrentYearDisposed ? "Disposed" : "Active"
                      }
                      size='small'
                      color={asset.isCurrentYearDisposed ? "error" : "success"}
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Stack direction='row' spacing={1} justifyContent='center'>
                      <Tooltip title='View Details'>
                        <IconButton
                          size='small'
                          onClick={() => onViewAsset(asset.assetId)}
                          color='primary'
                        >
                          <Visibility fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Edit Asset'>
                        <IconButton
                          size='small'
                          onClick={() => onEditAsset(asset.assetId)}
                          color='primary'
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Define Principal Paid'>
                        <IconButton
                          size='small'
                          onClick={() => onDefinePrincipalPaid(asset.assetId)}
                          color='info'
                        >
                          <Payments fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='More Actions'>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            setActionMenuAnchor(e.currentTarget);
                            setActionMenuAssetId(asset.assetId);
                          }}
                        >
                          <MoreVert fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {filteredAndSortedAssets.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Typography variant='body1' color='text.secondary' gutterBottom>
            {searchTerm || filterCaType !== "all"
              ? "No assets match your search criteria"
              : "No assets found"}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {searchTerm || filterCaType !== "all"
              ? "Try adjusting your search or filter"
              : "Add your first asset to get started"}
          </Typography>
        </Box>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => setFilterCaType("all")}>
          All Types ({assets.length})
        </MenuItem>
        {caTypes.map((type) => (
          <MenuItem key={type} onClick={() => setFilterCaType(type)}>
            {type} ({assets.filter((a) => a.caType === type).length})
          </MenuItem>
        ))}
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleSort("assetName")}>
          Asset Name {getSortIcon("assetName")}
        </MenuItem>
        <MenuItem onClick={() => handleSort("caType")}>
          CA Type {getSortIcon("caType")}
        </MenuItem>
        <MenuItem onClick={() => handleSort("cost")}>
          Cost {getSortIcon("cost")}
        </MenuItem>
        <MenuItem onClick={() => handleSort("noOfYears")}>
          Number of Years {getSortIcon("noOfYears")}
        </MenuItem>
      </Menu>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setActionMenuAssetId(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 220,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
          },
        }}
        TransitionComponent={Grow}
        transitionDuration={200}
      >
        <MenuItem
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
            cursor: "default",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <ListItemText
            primary='Actions'
            primaryTypographyProps={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "primary.main",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenuAssetId && onDuplicateAsset) {
              onDuplicateAsset(actionMenuAssetId);
            }
            setActionMenuAnchor(null);
            setActionMenuAssetId(null);
          }}
          sx={{
            transition: "all 0.15s ease",
            "&:hover": {
              transform: "translateX(4px)",
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize='small' color='primary' />
          </ListItemIcon>
          <ListItemText primary='Duplicate Asset' />
        </MenuItem>
        <Divider />
        <MenuItem
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05),
            cursor: "default",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05),
            },
          }}
        >
          <ListItemText
            primary='Remove'
            primaryTypographyProps={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "error.main",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const assetIndex = assets.findIndex(
              (a) => a.assetId === actionMenuAssetId
            );
            if (assetIndex !== -1) {
              onDeleteAsset(assetIndex);
            }
            setActionMenuAnchor(null);
            setActionMenuAssetId(null);
          }}
          sx={{
            color: "error.main",
            transition: "all 0.15s ease",
            "&:hover": {
              transform: "translateX(4px)",
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
            },
          }}
        >
          <ListItemIcon>
            <Delete fontSize='small' color='error' />
          </ListItemIcon>
          <ListItemText primary='Delete Asset' />
        </MenuItem>
      </Menu>

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteAssetDialog
        open={bulkDeleteDialogOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        assetName={
          selectedAssets.length === 1
            ? assets.find((a) => a.assetId === selectedAssets[0])?.assetName ||
              "Unknown Asset"
            : `${selectedAssets.length} assets`
        }
      />

      {/* Bulk Dispose Confirmation Dialog */}
      <WarningDialog
        open={bulkDisposeDialogOpen}
        onClose={handleCancelBulkDispose}
        onConfirm={handleConfirmBulkDispose}
        assetName={
          selectedAssets.length === 1
            ? assets.find((a) => a.assetId === selectedAssets[0])?.assetName ||
              "Unknown Asset"
            : `${selectedAssets.length} assets`
        }
        title='Change Disposal Status'
        confirmText='Confirm'
      >
        <WarningMessage
          body1={getBulkDisposeDialogMessage(selectedAssets)}
          body2={
            "This action will change the asset(s) status for the current year. This change can be reversed by editing the asset."
          }
        />
      </WarningDialog>
    </Paper>
  );
};

export default AssetListView;
